/**
 * @fileoverview Legal Consent Banner - ChartFlow Pro
 * @copyright © 2024 ChartFlow. All rights reserved.
 * 
 * Displays legal notices and collects user consent for terms of use.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Shield, FileText, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const LegalBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem('legal_acceptance');
    if (!accepted) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = async () => {
    const acceptance = {
      timestamp: new Date().toISOString(),
      version: '2.0',
      ip: window.location.hostname,
      userAgent: navigator.userAgent,
    };
    
    localStorage.setItem('legal_acceptance', JSON.stringify(acceptance));
    
    // Log acceptance server-side
    try {
      await supabase.rpc('log_security_event', {
        p_event_type: 'legal_acceptance',
        p_event_data: acceptance
      });
    } catch (error) {
      console.error('Failed to log legal acceptance:', error);
    }
    
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur border-t animate-in slide-in-from-bottom">
      <Card className="p-6 max-w-4xl mx-auto border-primary/20">
        <div className="flex items-start gap-4">
          <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                ⚖️ Protection juridique et confidentialité
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                En utilisant cette application, vous acceptez nos conditions d'utilisation,
                politique de confidentialité et vous reconnaissez que :
              </p>
            </div>
            
            <ul className="text-sm space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Le code source est protégé par copyright © 2024 ChartFlow</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Toute tentative de reverse engineering est strictement interdite</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Les données sont cryptées et protégées par des mesures de sécurité avancées</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>L'utilisation est soumise à notre licence commerciale</span>
              </li>
            </ul>
            
            <div className="flex gap-2 pt-2">
              <Button onClick={handleAccept} className="gap-2">
                <Lock className="w-4 h-4" />
                J'accepte les conditions
              </Button>
              <Button variant="outline" asChild>
                <a href="/legal/conditions-utilisation" target="_blank" className="gap-2">
                  <FileText className="w-4 h-4" />
                  Lire les CGU
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
